import {
  PropsWithChildren,
  useMemo,
  createContext,
  useContext,
} from 'react';
import {
  AddBrokerUserPayload, BrokerUser, CompanyUsers, defaultCompanyUser,
} from '../broker';
import { useManageUser } from './useManageUser';

export type ManageUserContextType = {
  companyId: string,
  companyUsers: CompanyUsers,
  openUsers: BrokerUser[],
  editingUserIds: string[],
  expandedRows: string[],
  isFetchingUsers: boolean,
  resetPagination: boolean,
  addNewUser: (user: BrokerUser) => void,
  deleteUser: (useId: string) => void,
  setEditUser: (useId: string) => void,
  unsetEditUser: (useId: string) => void,
  verifyUser: (user: AddBrokerUserPayload) => Promise<CompanyUsers>,
  updateUser: (user: AddBrokerUserPayload) => Promise<CompanyUsers>,
  reactivateUser: (userEmail: string) => Promise<CompanyUsers>,
  deactivateUser: (userEmail: string) => Promise<CompanyUsers>,
  resendRegistrationToken: (userEmail: string) => Promise<Record<string, any>>,
}

const defaultManageUserContext: ManageUserContextType = {
  companyId: '',
  companyUsers: defaultCompanyUser,
  openUsers: [],
  editingUserIds: [],
  expandedRows: [],
  isFetchingUsers: false,
  resetPagination: false,
  addNewUser: () => { },
  deleteUser: () => { },
  setEditUser: () => { },
  unsetEditUser: () => { },
  verifyUser: async () => defaultCompanyUser,
  updateUser: async () => defaultCompanyUser,
  reactivateUser: async () => defaultCompanyUser,
  deactivateUser: async () => defaultCompanyUser,
  resendRegistrationToken: async () => ({}),
};

export const ManageUserContext = createContext<ManageUserContextType>(
  defaultManageUserContext,
);

export function useManageUserContext() {
  return useContext(ManageUserContext);
}

export function ManageUserProvider({ id, children }: PropsWithChildren<{ id: string }>) {
  const manageUser = useManageUser(id);

  const contextValue = useMemo(() => ({ ...manageUser }), [manageUser]);

  return (
    <ManageUserContext.Provider value={contextValue}>
      {children}
    </ManageUserContext.Provider>
  );
}
